import React from 'react';
import { Link } from 'gatsby';

import SEO from '../components/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';

import heroPaddy from '../images/kalinga/hero-paddy.jpg';

import '../components/layout.css';

const IndexPage = () => (
  <div className="theme-default bg-background text-foreground">
    <SEO title="Home" />
    <Header />

    <main>
      {/* OCF mainHero: big light h1 + image */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-7 md:col-start-1">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight mb-6">
                Kalinga Restoration Project
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl mb-8">
                Turning rice husk into durable carbon removal for Indian
                agriculture.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/diligence" className="button-split group">
                  <span className="button-split-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                  <span className="button-split-pill">
                    Request diligence access
                  </span>
                </Link>
                <Link to="/projects/kalinga" className="button-secondary">
                  Explore the project
                </Link>
              </div>
            </div>
            <div className="md:col-span-5 md:col-start-8">
              <div className="rounded overflow-hidden border border-border">
                <img
                  src={heroPaddy}
                  alt="Rice paddy landscape in Odisha"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OCF spacer */}
      <div className="h-20 md:h-32" />

      {/* OCF problem/solution pattern: accent bar + statement */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-start">
            <div className="md:col-span-2 md:col-start-1 mt-2">
              <div className="accent-bar w-8 md:w-16" />
            </div>
            <div className="md:col-span-10 md:col-start-3">
              <h4 className="text-xl md:text-2xl leading-relaxed font-normal">
                Rice husk is an abundant agricultural residue in Odisha.
              </h4>
            </div>
            <div className="md:col-span-2 md:col-start-1 mt-2">
              <div className="accent-bar w-8 md:w-16 opacity-0" />
            </div>
            <div className="md:col-span-10 md:col-start-3">
              <h4 className="text-xl md:text-2xl leading-relaxed font-normal text-muted-foreground">
                We convert it into stable biochar for permanent soil carbon
                storage.
              </h4>
            </div>
          </div>
        </div>
      </section>

      {/* OCF spacer */}
      <div className="h-20 md:h-32" />

      {/* OCF twoUpLabelAndText: chip + prose */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4 md:col-start-1">
              <span className="chip">About</span>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              <div className="prose prose-lg max-w-none">
                <p>
                  The Kalinga Restoration Project converts rice husk, an
                  abundant agricultural residue in Odisha, into biochar through
                  controlled pyrolysis. The biochar is then incorporated into
                  agricultural soils, where its stable carbon is designed for
                  long-term storage while supporting soil health and
                  agricultural productivity.
                </p>
                <p>
                  LongStraw maintains comprehensive technical diligence packages
                  for carbon removal procurement desks, institutional investors,
                  and independent diligence rating agencies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OCF spacer */}
      <div className="h-20 md:h-32" />

      {/* OCF stats section */}
      <section className="section theme-default-raised">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="mb-12">
            <span className="chip mb-4 inline-flex">Project Capacity</span>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight mt-4">
              At a glance
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-background rounded border border-border p-6">
              <div className="text-4xl md:text-5xl font-light tracking-tight mb-2">
                <span className="count-up">100</span>
              </div>
              <div className="font-mono text-sm text-muted-foreground mb-2">
                tonnes / day
              </div>
              <p className="text-sm leading-relaxed">
                Biomass processing capacity across the two operational
                facilities, equivalent to ~25,000 tonnes/year of agricultural
                rice husk diverted from burning.
              </p>
            </div>
            <div className="bg-background rounded border border-border p-6">
              <div className="text-4xl md:text-5xl font-light tracking-tight mb-2">
                <span className="count-up">~7,500</span>
              </div>
              <div className="font-mono text-sm text-muted-foreground mb-2">
                tonnes / year
              </div>
              <p className="text-sm leading-relaxed">
                Expected biochar production capacity produced under controlled
                slow pyrolysis and optimized for soil application.
              </p>
            </div>
            <div className="bg-background rounded border border-border p-6">
              <div className="text-4xl md:text-5xl font-light tracking-tight mb-2">
                <span className="count-up">~20,000</span>
              </div>
              <div className="font-mono text-sm text-muted-foreground mb-2">
                tCO&#8322;e / year
              </div>
              <p className="text-sm leading-relaxed">
                Expected net carbon removal capacity verified under the Isometric
                Biochar Storage in Soil Environments Module targeting 200-year
                durability.
              </p>
            </div>
            <div className="bg-background rounded border border-border p-6">
              <div className="text-4xl md:text-5xl font-light tracking-tight mb-2">
                <span className="count-up">200</span>
              </div>
              <div className="font-mono text-sm text-muted-foreground mb-2">
                year durability
              </div>
              <p className="text-sm leading-relaxed">
                Permanence tier verified under the Isometric Biochar Storage in
                Soil Environments Module.
              </p>
            </div>
          </div>
          <div className="mt-8 bg-background rounded border border-border p-6">
            <div className="font-mono text-sm text-muted-foreground mb-2">
              Soil amendment
            </div>
            <p className="text-sm leading-relaxed">
              <strong>MatiBoost&#8482;</strong>, a biochar-enhanced soil
              amendment product connecting durable carbon removal directly with
              smallholder farm soil regeneration across Odisha.
            </p>
          </div>
        </div>
      </section>

      {/* OCF spacer */}
      <div className="h-20 md:h-32" />

      {/* OCF projects section */}
      <section className="section">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4 md:col-start-1">
              <span className="chip">Projects</span>
            </div>
            <div className="md:col-span-8 md:col-start-5">
              <div className="bg-background-raised rounded border border-border p-8">
                <h3 className="text-2xl md:text-3xl font-light tracking-tight mb-4">
                  Project Kalinga
                </h3>
                <p className="text-base leading-relaxed mb-6">
                  Project Kalinga is LongStraw&apos;s operational biochar
                  programme in Odisha, India. Technology: controlled pyrolysis.
                  Feedstock: 100% rice husk. Permanence tier: 200-year
                  durability. Operating bases: Kalahandi &amp; Sambalpur.
                  Commercial status: operational (2026).
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/projects/kalinga" className="button-split group">
                    <span className="button-split-circle">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </span>
                    <span className="button-split-pill">
                      Learn more about Project Kalinga
                    </span>
                  </Link>
                  <Link to="/diligence" className="button-secondary">
                    Request diligence access
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OCF spacer */}
      <div className="h-20 md:h-32" />

      {/* OCF bottomCTA: theme-orange */}
      <section className="section theme-orange">
        <div className="max-w-screen-xpad mx-auto px-4 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-7 md:col-start-6">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-8 leading-tight">
                Ready to explore Project Kalinga?
              </h2>
              <div className="flex flex-wrap gap-4">
                <Link to="/diligence" className="button-split group">
                  <span className="button-split-circle">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="2"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                      />
                    </svg>
                  </span>
                  <span className="button-split-pill">
                    Request diligence access
                  </span>
                </Link>
                <Link to="/contact" className="button-secondary">
                  Contact us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default IndexPage;
